import React, { useEffect } from 'react';
import { useInsuranceOperationCreateMutation } from '../../../../graphql/graphql';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import NCZIReferenceSearchSelectField from '../../../Nczi/Components/ReferenceSearchSelect/NCZIReferenceSearchSelectField';
import { NCZI_OID } from '../../../Nczi/Enums/ncziOid.enum';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import NumberField from '../../../../Components/Form/components/NumberField';
import InputField from '../../../../Components/Form/components/InputField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import TextAreaField from '../../../../Components/Form/components/TextAreaField';
import useTranslation from '../../../../Hooks/useTranslation';
import useNotification from '../../../../Hooks/useNotification.hook';
import useApiClient from '../../../Base/Hooks/useApiClient';

interface Props {
	insuranceId: string;
	rootWorkplaceId: string;
	onCreate: () => void;
}

const InsuranceCompanyAddOperation: React.FC<Props> = ({
	insuranceId,
	rootWorkplaceId,
	onCreate,
}) => {
	const { t } = useTranslation();
	const { showNotificationSuccess } = useNotification();
	const { displayGQLError } = useApiClient();
	const [createInsuranceOperation] = useInsuranceOperationCreateMutation();

	const validationSchema = yup.object({
		operation: yup
			.mixed()
			.required(t('validation.insuranceCompanyAddOperation.operation')),
		code: yup
			.string()
			.required(t('validation.insuranceCompanyAddOperation.code')),
		title: yup
			.string()
			.required(t('validation.insuranceCompanyAddOperation.title')),
		points: yup
			.number()
			.required(t('validation.insuranceCompanyAddOperation.points'))
			.typeError(
				t('validation.insuranceCompanyAddOperation.pointsNumber') ?? '',
			),
		pricePerPoint: yup
			.number()
			.required(t('validation.insuranceCompanyAddOperation.pricePerPoint'))
			.typeError(
				t('validation.insuranceCompanyAddOperation.pricePerPointNumber') ?? '',
			),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const operationValue = formMethods.watch('operation');

	useEffect(() => {
		const operationLabel = operationValue?.label;
		if (operationLabel) {
			const splitedOperation = operationLabel.split(': ');
			formMethods.setValue('code', splitedOperation[0]);
			formMethods.setValue('title', splitedOperation[1]);
		}
	}, [operationValue]);

	const handleSubmit = async (values: any) => {
		const result = await createInsuranceOperation({
			input: {
				insuranceId,
				workplaceId: rootWorkplaceId,
				ncziCode: values?.operation?.value?.code,
				title: values?.title,
				code: values?.code,
				points: parseInt(values?.points),
				pricePerPoint: parseFloat(values?.pricePerPoint),
			},
		});
		displayGQLError(result);
		if ('data' in result) {
			showNotificationSuccess({
				content: t('component.insuranceCompanyAddOperation.successAddMessage'),
			});
			onCreate();
		}
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<InputWrapper>
				<NCZIReferenceSearchSelectField
					label='component.insuranceCompanyAddOperation.operation'
					name='operation'
					oid={NCZI_OID.OPERATION}
					showAbbr
					isRequired
				/>
			</InputWrapper>
			<InputField
				name='code'
				label='component.insuranceCompanyAddOperation.code'
				isRequired
				isWrapped
			/>
			<InputWrapper>
				<TextAreaField
					name='title'
					label='component.insuranceCompanyAddOperation.title'
					isRequired
				/>
			</InputWrapper>
			<InputWrapper>
				<NumberField
					name='points'
					label='component.insuranceCompanyAddOperation.points'
					isRequired
				/>
			</InputWrapper>
			<InputWrapper>
				<NumberField
					name='pricePerPoint'
					label='component.insuranceCompanyAddOperation.pricePerPoint'
					isRequired
				/>
			</InputWrapper>
			<SubmitButton onSubmit={handleSubmit} title='base.create' />
		</FormWrapper>
	);
};

export default InsuranceCompanyAddOperation;
